import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { Input } from '@heidi/design-system/input';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { isPlausibleEmail } from './extract-email';

import { InlineApprovalActions } from '../../inline-approval/inline-approval-actions';
import type { ApprovalState } from '../store/useVoiceAssistantStore';
import { voiceAssistantMessages as messages } from './voice-assistant.messages';

interface VoiceAssistantApprovalTopBarProps {
  approval: ApprovalState | null;
  onResolve: (approved: boolean, modifiedInput?: Record<string, unknown>) => void;
  /** Extra classes on the floating layer, e.g. a width cap in a wider host. */
  className?: string;
}

/** Floating inset bar above the Talk-to-Heidi chrome (overlays transcript). */
export function VoiceAssistantApprovalTopBar({
  approval,
  onResolve,
  className,
}: VoiceAssistantApprovalTopBarProps) {
  const intl = useIntl();
  const reduceMotion = useReducedMotion();
  const isVisible = approval !== null;
  const summary = approval?.summary ?? intl.formatMessage(messages.approvalFallback);
  const initialEmails = approval?.toEmails ?? [];
  const showEmailField = initialEmails.length > 0;
  const [emailDraft, setEmailDraft] = useState(() => initialEmails.join(', '));

  useEffect(() => {
    setEmailDraft((approval?.toEmails ?? []).join(', '));
  }, [approval]);

  const parsedEmails = emailDraft
    .split(/[,;\s]+/)
    .map((e) => e.trim())
    .filter(Boolean);
  const emailValid =
    !showEmailField ||
    (parsedEmails.length > 0 && parsedEmails.every((e) => isPlausibleEmail(e)));

  const handleResolve = (approved: boolean): void => {
    if (!approved) {
      onResolve(false);
      return;
    }
    if (!showEmailField) {
      onResolve(true);
      return;
    }
    if (!emailValid) return;
    const unchanged =
      parsedEmails.length === initialEmails.length &&
      parsedEmails.every((e, i) => e === initialEmails[i]);
    onResolve(true, unchanged ? undefined : { to_emails: parsedEmails });
  };

  return (
    <AnimatePresence>
      {isVisible ? (
        <m.div
          data-testid="voice-assistant-approval-top-float"
          role="group"
          aria-label={intl.formatMessage(messages.approvalTitle)}
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{
            opacity: 0,
            y: 8,
            transition: { duration: 0.15, ease: 'easeOut' },
          }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-full z-20',
            className
          )}
        >
          <div className="pointer-events-auto mx-3 mb-2 flex flex-col gap-2 rounded-2xl border border-border bg-surface px-4 py-3 shadow-sm">
            <div className="flex items-end gap-3">
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center gap-1.5">
                  <HeidiLogoMark className="size-3 text-text-secondary" />
                  <TypographyCaption className="text-text-tertiary">
                    {intl.formatMessage(messages.approvalTitle)}
                  </TypographyCaption>
                </div>
                <TypographyP2
                  className="line-clamp-2 text-pretty break-words text-text-primary"
                  title={summary}
                >
                  {summary}
                </TypographyP2>
              </div>
              <InlineApprovalActions
                onApprove={() => handleResolve(true)}
                onDeny={() => handleResolve(false)}
                pendingVerdict={null}
                approveDisabled={!emailValid}
                approveLabel={intl.formatMessage(messages.approvalConfirm)}
                className="shrink-0"
              />
            </div>
            {showEmailField ? (
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="voice-approval-recipient-email"
                  className="text-[11px] font-medium text-text-secondary"
                >
                  {intl.formatMessage(messages.approvalRecipientEmail)}
                </label>
                <Input
                  id="voice-approval-recipient-email"
                  type="email"
                  autoComplete="email"
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  data-testid="voice-approval-recipient-email"
                  className="h-8 text-xs"
                />
              </div>
            ) : null}
          </div>
        </m.div>
      ) : null}
    </AnimatePresence>
  );
}
